/**
 * Client-side list filters for the Actions tab. Conditions stack: values
 * checked within a field are OR'd, and fields / search / dates / area are
 * AND'd.
 */

import {
  resolveActionAssignee,
  UNASSIGNED_LABEL,
  type AssigneeDirectory,
} from './actionEditModel';
import { ACTION_COLUMN_LABELS } from './actionColumnModel';
import { blockingCount } from '../utils/actionDependencies';
import {
  filterActionsInArea,
  type ActionAreaFilter,
} from '../utils/actionSpatialFilter';
import type { ActionEntity } from '../hooks/useScopedActionForm';
import type { IapScope } from '../ICSConstants';
import { normalizeChecklistItems } from '../actionChecklistModel';
import { needsMyApproval } from './taskApprovalFilter';

export const ACTION_FILTER_UNASSIGNED = 'unassigned';
export const ACTION_FILTER_NONE = '__none__';
export const ACTION_FILTER_BLOCKING = 'blocking';
export const ACTION_FILTER_NOT_BLOCKING = 'not_blocking';
export const ACTION_FILTER_YES = 'yes';
export const ACTION_FILTER_NO = 'no';

export type ActionFilterFieldKey =
  | 'status'
  | 'priority'
  | 'assignee'
  | 'aor'
  | 'briefed_poc'
  | 'blocking'
  | 'workspace';

export type ActionListFilters = {
  search: string;
  selected: Record<string, string[]>;
  startFrom: string;
  startTo: string;
  dueFrom: string;
  dueTo: string;
  mine: boolean;
  /** HWCG: only tasks whose next approve step a seat the user holds can take. */
  needsApproval: boolean;
};

export const EMPTY_ACTION_LIST_FILTERS: ActionListFilters = {
  search: '',
  selected: {},
  startFrom: '',
  startTo: '',
  dueFrom: '',
  dueTo: '',
  mine: false,
  needsApproval: false,
};

export type ActionFilterOption = {
  value: string;
  label: string;
};

export type ActionFilterColumn = {
  key: ActionFilterFieldKey;
  label: string;
  options: ActionFilterOption[];
};

export type ActionFilterChip = {
  id: string;
  label: string;
};

const INCIDENT_ONLY_FIELDS: readonly ActionFilterFieldKey[] = ['workspace'];

const FIELD_ORDER: ActionFilterFieldKey[] = [
  'status',
  'priority',
  'assignee',
  'aor',
  'briefed_poc',
  'blocking',
  'workspace',
];

type LocatableAction = ActionEntity & {
  display_id?: string | null;
  aor_id?: string | null;
  aor_name?: string | null;
  workspace_name?: string | null;
  geolocation?: { address?: string; name?: string } | null;
};

function asLocatable(action: ActionEntity): LocatableAction {
  return action as LocatableAction;
}

export function actionLocationLabel(action: ActionEntity): string {
  const geolocation = asLocatable(action).geolocation;
  return geolocation?.address || geolocation?.name || '';
}

function searchHaystack(action: ActionEntity): string {
  const row = asLocatable(action);
  return [
    row.display_id,
    action.name,
    action.description,
    actionLocationLabel(action),
  ]
    .filter((value): value is string => Boolean(value))
    .join(' ')
    .toLowerCase();
}

export function actionMatchesSearch(
  action: ActionEntity,
  search: string
): boolean {
  const term = search.trim().toLowerCase();
  if (!term) return true;
  return searchHaystack(action).includes(term);
}

function filterBoundMs(localDateTime: string): number | null {
  if (!localDateTime) return null;
  const [datePart, timePart] = localDateTime.split('T');
  if (!datePart || datePart.length < 10) return null;
  const [year, month, day] = datePart.split('-').map(Number);
  if (!year || !month || !day) return null;
  const [hour, minute] = (timePart ?? '00:00').split(':').map(Number);
  return new Date(year, month - 1, day, hour || 0, minute || 0).getTime();
}

export function actionMatchesDateRange(
  dateValue: Date | string | null | undefined,
  from: string,
  to: string
): boolean {
  if (!from && !to) return true;
  if (!dateValue) return false;
  const eventMs = new Date(dateValue).getTime();
  if (Number.isNaN(eventMs)) return false;
  const fromMs = filterBoundMs(from);
  const toMs = filterBoundMs(to);
  if (fromMs != null && eventMs < fromMs) return false;
  if (toMs != null && eventMs > toMs) return false;
  return true;
}

function chipDateTimeLabel(value: string): string {
  return value.replace('T', ' ').slice(0, 16);
}

export function actionFilterValues(
  action: ActionEntity,
  key: ActionFilterFieldKey,
  directory: AssigneeDirectory,
  allActions: ActionEntity[]
): string[] {
  const row = asLocatable(action);
  switch (key) {
    case 'status':
      return row.status ? [row.status] : [ACTION_FILTER_NONE];
    case 'priority':
      return row.priority ? [row.priority] : [ACTION_FILTER_NONE];
    case 'assignee': {
      const resolved = resolveActionAssignee(action, directory);
      return resolved.id
        ? [`${resolved.kind}:${resolved.id}`]
        : [ACTION_FILTER_UNASSIGNED];
    }
    case 'aor':
      return row.aor_id ? [row.aor_id] : [ACTION_FILTER_NONE];
    case 'briefed_poc':
      return [row.briefed_poc ? ACTION_FILTER_YES : ACTION_FILTER_NO];
    case 'blocking':
      return [
        blockingCount(action.action_id, allActions) > 0
          ? ACTION_FILTER_BLOCKING
          : ACTION_FILTER_NOT_BLOCKING,
      ];
    case 'workspace':
      return row.workspace_name ? [row.workspace_name] : [ACTION_FILTER_NONE];
    default:
      return [];
  }
}

function optionLabel(
  key: ActionFilterFieldKey,
  value: string,
  action: ActionEntity,
  directory: AssigneeDirectory,
  relabel: (value: string) => string
): string {
  if (value === ACTION_FILTER_NONE) {
    if (key === 'aor') return relabel('No AOR');
    if (key === 'workspace') return 'No workspace';
    if (key === 'status') return 'No status';
    if (key === 'priority') return 'No priority';
    return 'None';
  }
  if (key === 'assignee') {
    if (value === ACTION_FILTER_UNASSIGNED) return UNASSIGNED_LABEL;
    return resolveActionAssignee(action, directory).label;
  }
  if (key === 'aor') {
    return asLocatable(action).aor_name?.trim() || relabel('AOR');
  }
  if (key === 'briefed_poc') {
    return value === ACTION_FILTER_YES ? 'Yes' : 'No';
  }
  if (key === 'blocking') {
    return value === ACTION_FILTER_BLOCKING ? 'Blocking' : 'Not blocking';
  }
  return value;
}

export function buildActionFilterColumns(
  actions: ActionEntity[],
  scope: IapScope,
  directory: AssigneeDirectory,
  relabel: (value: string) => string = (value) => value
): ActionFilterColumn[] {
  return FIELD_ORDER.filter(
    (key) => scope === 'incident' || !INCIDENT_ONLY_FIELDS.includes(key)
  ).map((key) => {
    const seen = new Map<string, string>();
    actions.forEach((action) => {
      actionFilterValues(action, key, directory, actions).forEach((value) => {
        if (!seen.has(value)) {
          seen.set(value, optionLabel(key, value, action, directory, relabel));
        }
      });
    });
    const options = [...seen.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
    return {
      key,
      label:
        key === 'aor'
          ? relabel(ACTION_COLUMN_LABELS[key])
          : ACTION_COLUMN_LABELS[key],
      options,
    };
  });
}

export function actionMatchesFieldFilters(
  action: ActionEntity,
  selected: Record<string, string[]>,
  directory: AssigneeDirectory,
  allActions: ActionEntity[]
): boolean {
  return FIELD_ORDER.every((key) => {
    const values = selected[key] ?? [];
    if (values.length === 0) return true;
    const actionValues = actionFilterValues(action, key, directory, allActions);
    return actionValues.some((value) => values.includes(value));
  });
}

export function collectMineActionIds(
  serverActionIds: Iterable<string>,
  actions: ActionEntity[],
  userGuid: string
): Set<string> {
  const ids = new Set(serverActionIds);
  if (!userGuid) return ids;
  actions.forEach((action) => {
    const items = normalizeChecklistItems(action.checklist);
    if (items.some((item) => item.responsible_individual_id === userGuid)) {
      ids.add(String(action.action_id));
    }
  });
  return ids;
}

export function actionMatchesMine(
  action: ActionEntity,
  mine: boolean,
  mineActionIds: ReadonlySet<string>
): boolean {
  if (!mine) return true;
  return mineActionIds.has(String(action.action_id));
}

export function actionMatchesNeedsApproval(
  action: ActionEntity,
  needsApproval: boolean,
  positionNames: readonly string[]
): boolean {
  if (!needsApproval) return true;
  return needsMyApproval(action, positionNames);
}

export function filterActions<T extends ActionEntity>(
  actions: T[],
  filters: ActionListFilters,
  directory: AssigneeDirectory,
  area: ActionAreaFilter | null,
  mineActionIds: ReadonlySet<string> = new Set(),
  userPositionNames: readonly string[] = []
): T[] {
  const inArea = filterActionsInArea(actions, area?.geometry);
  return inArea.filter(
    (action) =>
      actionMatchesSearch(action, filters.search) &&
      actionMatchesDateRange(
        action.start_date,
        filters.startFrom,
        filters.startTo
      ) &&
      actionMatchesDateRange(
        action.target_date,
        filters.dueFrom,
        filters.dueTo
      ) &&
      actionMatchesFieldFilters(action, filters.selected, directory, actions) &&
      actionMatchesMine(action, filters.mine, mineActionIds) &&
      actionMatchesNeedsApproval(
        action,
        filters.needsApproval,
        userPositionNames
      )
  );
}

export function hasActionNarrowingFilters(
  filters: ActionListFilters,
  area: ActionAreaFilter | null
): boolean {
  return (
    filters.search.trim().length > 0 ||
    filters.startFrom.length > 0 ||
    filters.startTo.length > 0 ||
    filters.dueFrom.length > 0 ||
    filters.dueTo.length > 0 ||
    Object.values(filters.selected).some((values) => values.length > 0) ||
    filters.mine ||
    filters.needsApproval ||
    area != null
  );
}

export function toggleActionFilterValue(
  selected: Record<string, string[]>,
  key: string,
  value: string
): Record<string, string[]> {
  const current = selected[key] ?? [];
  const next = current.includes(value)
    ? current.filter((existing) => existing !== value)
    : [...current, value];
  const copy = { ...selected };
  if (next.length === 0) {
    delete copy[key];
  } else {
    copy[key] = next;
  }
  return copy;
}

function fieldChipId(key: string, value: string): string {
  return `field:${key}:${encodeURIComponent(value)}`;
}

export function buildActionFilterChips(
  filters: ActionListFilters,
  columns: ActionFilterColumn[],
  area: ActionAreaFilter | null
): ActionFilterChip[] {
  const chips: ActionFilterChip[] = [];
  if (filters.mine) {
    chips.push({ id: 'mine', label: 'My Tasks' });
  }
  if (filters.needsApproval) {
    chips.push({ id: 'needsApproval', label: 'Needs My Approval' });
  }
  const search = filters.search.trim();
  if (search) {
    chips.push({ id: 'search', label: `Search: ${search}` });
  }
  if (filters.startFrom) {
    chips.push({
      id: 'startFrom',
      label: `Start from ${chipDateTimeLabel(filters.startFrom)}`,
    });
  }
  if (filters.startTo) {
    chips.push({
      id: 'startTo',
      label: `Start to ${chipDateTimeLabel(filters.startTo)}`,
    });
  }
  if (filters.dueFrom) {
    chips.push({
      id: 'dueFrom',
      label: `Due from ${chipDateTimeLabel(filters.dueFrom)}`,
    });
  }
  if (filters.dueTo) {
    chips.push({
      id: 'dueTo',
      label: `Due to ${chipDateTimeLabel(filters.dueTo)}`,
    });
  }
  columns.forEach((column) => {
    (filters.selected[column.key] ?? []).forEach((value) => {
      const option = column.options.find((item) => item.value === value);
      chips.push({
        id: fieldChipId(column.key, value),
        label: `${column.label}: ${option?.label ?? value}`,
      });
    });
  });
  if (area) {
    chips.push({ id: 'area', label: `Area: ${area.label}` });
  }
  return chips;
}

export function dismissActionListFilterChip(
  filters: ActionListFilters,
  chipId: string
): ActionListFilters {
  if (chipId === 'mine') return { ...filters, mine: false };
  if (chipId === 'needsApproval') return { ...filters, needsApproval: false };
  if (chipId === 'search') return { ...filters, search: '' };
  if (chipId === 'startFrom') return { ...filters, startFrom: '' };
  if (chipId === 'startTo') return { ...filters, startTo: '' };
  if (chipId === 'dueFrom') return { ...filters, dueFrom: '' };
  if (chipId === 'dueTo') return { ...filters, dueTo: '' };
  if (chipId.startsWith('field:')) {
    const rest = chipId.slice('field:'.length);
    const sep = rest.indexOf(':');
    if (sep < 0) return filters;
    const key = rest.slice(0, sep);
    const value = decodeURIComponent(rest.slice(sep + 1));
    return {
      ...filters,
      selected: toggleActionFilterValue(filters.selected, key, value),
    };
  }
  return filters;
}
